<template>
  <section
    v-if="issue"
    class="page-issue"
  >
    <div class="page-issue__content">
      <div class="page-issue__description">
        <h6>{{ $t('issue_description_title') }}</h6>
        <div class="description markdown-body">
          <div v-html="issue.descriptionHtml || $t('message.description_empty')"></div>
        </div>
      </div>
      <div
        class="page-issue__discussions"
        id="discussions"
        ref="discussions"
      >
        <div class="title">
          <h5>{{ $t('message.comments') }}</h5>
          <div class="actions">
            <gf-button
              v-if="getActionAvailabilityFlag(ACTION_ENUM.CREATE_ISSUE_NOTE) && !isNewDiscussionCreating"
              variant="primary"
              :text="$t('message.add')"
              @click="onCreateDiscussionClick"
            />
          </div>
        </div>
        <div
          class="content"
          v-if="getActionAvailabilityFlag(ACTION_ENUM.READ_ISSUE_NOTE)"
        >
          <issue-discussion-form
            v-if="isNewDiscussionCreating"
            ref="createDiscussionForm"
            :is-reply="false"
            @close="onNewDiscussionFormClose"
            @submit="onNewDiscussionSubmit"
          />
          <issue-discussions v-if="getActionAvailabilityFlag(ACTION_ENUM.READ_ISSUE_NOTE)" />
        </div>
      </div>
    </div>
    <div class="page-issue__aside">
      <div
        v-if="hasStatusEditAccess"
        class="form --status"
      >
        <b class="title">{{ $tc('message.status', 0) }}</b>
        <div class="content">
          <custom-select
            :placeholder="$tc('message.status', 2)"
            :options="statusReference"
            v-model="selectedStatusModel"
            @input="changeIssueStatus"
          >
            <custom-selector-dot-option
              slot-scope="{ option }"
              :hex-color="option.hexColor"
              :option="option"
            />
          </custom-select>
        </div>
      </div>

      <div
        v-if="issue.assignedUsers?.length > 0 || hasEditAccess"
        class="form --assigned"
      >
        <b class="title">{{ $tc('message.assigned', 0) }}</b>
        <div
          v-if="issue.assignedUsers?.length > 0"
          class="content"
        >
          <user-label-item
            v-for="user in issue.assignedUsers"
            :key="user.id"
            :user="user"
            :has-user-link="true"
            :open-in-new-tab="false"
            image-size="38"
          />
        </div>
        <div
          v-else
          class="content"
        >
          <small class="muted">{{ $t('assign_assigners') }}</small>
        </div>
      </div>

      <div
        v-if="issue?.labels?.length > 0 || hasEditAccess"
        class="form --labels"
      >
        <b class="title">Лейблы</b>
        <div
          v-if="issue.labels?.length > 0"
          class="content"
        >
          <repo-label-list :labels="issue.labels" />
        </div>
        <div
          v-else
          class="content"
        >
          <small class="muted">{{ $t('add_labels') }}</small>
        </div>
      </div>

      <div
        v-if="issue.assignedMergeRequests?.labels > 0 || getActionAvailabilityFlag(ACTION_ENUM.READ_MERGE_REQUEST)"
        class="form --merge-requests"
      >
        <b
          class="title"
          v-if="issue.assignedMergeRequests?.length > 0"
        >
          {{ $t('linked_merge_requests') }}
        </b>
        <div class="content">
          <div
            class="list"
            v-if="issue.assignedMergeRequests?.length > 0"
          >
            <merge-request-card-short
              v-for="mergeRequest in issue.assignedMergeRequests"
              :key="mergeRequest.id"
              :merge-request="mergeRequest"
            />
          </div>
          <gf-button
            v-if="
              issue?.assignedMergeRequests?.length < 10 && getActionAvailabilityFlag(ACTION_ENUM.READ_MERGE_REQUEST)
            "
            class="link-request-button"
            variant="success"
            :text="$t('link_request')"
            @click="openLinkRequestModal"
          ></gf-button>
        </div>
      </div>

      <div
        v-if="issue?.updatedAt || issue?.createdAt"
        class="form --created"
      >
        <div class="content">
          <span
            v-if="issue?.updatedAt?.length > 0"
            class="updated-at"
          >
            <i class="gf-icon gf-icon-clock gf-fs-2 muted"></i>
            <small>
              {{ $tc('message.update', 0).toLowerCase() }}
              {{ $moment(issue?.updatedAt)?.format('DD.MM.YY [в] HH:mm') }}
            </small>
          </span>
          <span
            v-if="issue?.createdAt?.length > 0"
            class="created-at"
          >
            <i class="gf-icon gf-icon-calendar gf-fs-2 muted"></i>
            <small>
              {{ $tc('message.created', 0).toLowerCase() }}
              {{ $moment(issue?.createdAt)?.format('DD.MM.YY [в] HH:mm') }}
            </small>
          </span>
        </div>
      </div>
      <div class="form --more">
        <div class="about">
          <a
            href="https://docs.gitflic.ru/project/issue"
            target="_blank"
          >
            {{ $t('about') }}
          </a>
          <span class="about-icon">
            <i class="gf-icon gf-icon-arrow-outward gf-fs-2"></i>
          </span>
        </div>
      </div>
      <gf-custom-modal-window
        v-if="getActionAvailabilityFlag(ACTION_ENUM.READ_MERGE_REQUEST) && issue?.assignedMergeRequests?.length < 10"
        ref="linkRequestModal"
        gf-modal-data-name="linkMergeRequestModal"
        :gf-modal-header="$t('link_issue_to_request')"
        gf-modal-wrapper-width="600px"
        @close="onLinkMergeRequestModalClose"
      >
        <template #body>
          <link-merge-request-form
            ref="linkRequestForm"
            @cancel="closeLinkRequestModal"
            @submit="linkNewMergeRequest"
          />
        </template>
      </gf-custom-modal-window>
    </div>
  </section>
</template>

<script>
import MergeRequestService from '@shared/modules/merge-request/merge-request.service';
import ACTION_ENUM from '@shared/modules/custom-roles/custom-roles.constants';
import IssueDiscussionForm from '@shared/modules/issue/forms/item/IssueDiscussionForm';
import IssueDiscussions from '@shared/modules/issue/components/item/IssueDiscussions';
import UserLabelItem from '@shared/shared-components/shared/user-label-item';
import RepoLabelList from '@shared/shared-components/shared/repo-label-list';
import GfButton from '@/shared-modules/shared-components/shared/components/GfButton';
import IssueMessages from '@shared/modules/issue/issue.messages';
import MergeRequestCardShort from '@/shared-modules/modules/merge-request/components/MergeRequestCardShort';
import IssueService from '@shared/modules/issue/issue.service';
import RefS from '@shared/services/reference.service';
import { smallNotification } from '@shared/services/gf.notification.service';
import CustomSelectorDotOption from '@shared/shared-components/shared/custom-selector-dot-option';
import CustomSelect from '@shared/shared-components/shared/custom-select';
import GfCustomModalWindow from '@/shared-modules/shared-components/shared/GfCustomModalWindow';
import LinkMergeRequestForm from '@shared/modules/issue/forms/item/LinkMergeRequestForm';
import { FORM_NAME_ENUMS } from '@/shared-modules/variables/form-name.enums';

export default {
  name: 'PageIssue',
  components: {
    GfButton,
    IssueDiscussionForm,
    IssueDiscussions,
    UserLabelItem,
    RepoLabelList,
    MergeRequestCardShort,
    CustomSelectorDotOption,
    CustomSelect,
    GfCustomModalWindow,
    LinkMergeRequestForm
  },
  data() {
    return {
      isNewDiscussionCreating: false,
      selectedStatusModel: null
    };
  },
  beforeRouteEnter(to, from, next) {
    Promise.all([RefS.getSourceProjectForks(to.params.alias, to.params.projectAlias)]).finally(next);
  },
  watch: {
    '$route.query': function () {
      this.getIssueDiscussions();
    }
  },
  computed: {
    alias() {
      return this.$route.params?.alias;
    },
    projectAlias() {
      return this.$route.params?.projectAlias;
    },
    issueId() {
      return this.$route.params?.issueId;
    },
    issue() {
      return this.$store.getters.getIssue || {};
    },
    ACTION_ENUM() {
      return ACTION_ENUM;
    },
    hasEditAccess() {
      return document.getElementById('hasEditAccess')?.getAttribute('value') === 'true';
    },
    hasStatusEditAccess() {
      return (
        this.hasEditAccess ||
        (document.getElementById('hasStatusEditAccess') &&
          document.getElementById('hasStatusEditAccess').value === 'true')
      );
    },
    statusReference() {
      return this.$store.getters.getRefIssueStatus;
    },
    discussionsCount() {
      return this.$store.getters.getDiscussionsCounter;
    }
  },
  mounted() {
    this.getIssueReferenceStatuses();
    this.selectedStatusModel = this.issue?.status;
    if (this.getActionAvailabilityFlag(ACTION_ENUM.READ_ISSUE_NOTE)) {
      this.getIssueDiscussions();
    }
  },
  methods: {
    getMergeRequest() {
      MergeRequestService.getMergeRequest(this.alias, this.projectAlias, this.mergeRequestId);
    },
    getIssue() {
      IssueService.getIssue(this.alias, this.projectAlias, this.issueId).then((res) => {
        this.selectedStatusModel = res.status;
      });
    },
    getIssueReferenceStatuses() {
      RefS.getRefIssueStatuses(this.alias, this.projectAlias);
    },
    getIssueDiscussions() {
      IssueService.getDiscussions(this.alias, this.projectAlias, this.issueId, this.$route.query).then(() => {
        if (this.$route.hash?.length > 0) {
          this.$refs.discussions.scrollIntoView({ block: 'start' });
        }
      });
    },
    changeIssueStatus(data) {
      IssueService.issueChangeStatus(this.alias, this.projectAlias, this.issueId, data).then(() => {
        smallNotification('success', { title: 'Статус обновлен!' });
        this.getIssue();
      });
    },
    onCreateDiscussionClick() {
      this.isNewDiscussionCreating = true;
      this.$nextTick(() => {
        this.$refs.createDiscussionForm.openEditor();
      });
    },
    onNewDiscussionFormClose() {
      this.isNewDiscussionCreating = false;
    },
    onNewDiscussionSubmit(data) {
      IssueService.postComment(this.issue.userAlias, this.issue.projectAlias, this.issue.localId, {
        note: data
      }).then((res) => {
        this.getIssueDiscussions();
        if (this.formErrors && this.formErrors.note) delete this.formErrors.note;
        this.$store.commit('addDiscussionsCounter', this.discussionsCount + 1);
        this.isNewDiscussionCreating = false;
      });
    },
    openLinkRequestModal() {
      this.$refs.linkRequestForm.getProjectForks();
      this.$refs.linkRequestModal.openModalWindow();
    },
    closeLinkRequestModal() {
      this.$refs.linkRequestModal.closeModalWindow();
    },
    onLinkMergeRequestModalClose() {
      this.$refs.linkRequestForm.resetSelectedValues();
      this.resetLinkRequestErrors();
    },
    linkNewMergeRequest(data) {
      IssueService.createLinkedMergeRequest(this.alias, this.projectAlias, this.issueId, data).then((res) => {
        this.$refs.linkRequestModal.closeModalWindow();
        location.href = `/project/${res?.targetProject?.ownerAlias}/${res?.targetProject?.alias}/merge-request/create?compare=${res?.sourceBranch?.title}&sourceProjectId=${res?.sourceProject?.id}&linkedIssue=${this.issueId}`;
      });
    },
    getActionAvailabilityFlag(action) {
      return this.$store.getters.getActionAvailabilityFlag(action);
    },
    resetLinkRequestErrors() {
      this.$store.dispatch('setFormErrors', {
        formName: FORM_NAME_ENUMS.LINKED_MERGE_REQUEST_FORM,
        errorResponse: null
      });
    }
  },
  i18n: {
    messages: IssueMessages
  }
};
</script>

<style lang="scss">
$this: '.page-issue';

#{$this} {
  display: flex;
  gap: 32px;

  @include theme.max-lg {
    flex-direction: column-reverse;
  }

  #{$this}__content {
    min-width: calc(100% - 322px);
    display: flex;
    flex-direction: column;
    gap: 24px;

    @include theme.max-xxl {
      min-width: calc(100% - 312px);
    }

    @include theme.max-xl {
      min-width: calc(100% - 252px);
    }

    @include theme.max-lg {
      min-width: 100%;
    }

    #{$this}__description {
      & > h6 {
        margin-bottom: 4px;
      }

      & > .description {
        padding: 16px;
        word-break: break-word;
        border: 1px solid var(--border-color);
        border-radius: 6px;
      }
    }

    #{$this}__discussions {
      display: flex;
      flex-direction: column;
      gap: 20px;

      & > .title {
        min-height: 34px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;

        & > h5 {
          margin-bottom: 0;
        }
      }

      & > .content {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
    }
  }

  #{$this}__aside {
    min-width: 290px;
    display: flex;
    flex-direction: column;
    gap: 24px;

    @include theme.max-xxl {
      min-width: 280px;
    }

    @include theme.max-xl {
      min-width: 220px;
    }

    @include theme.max-lg {
      min-width: 100%;
      gap: 16px;
    }

    & > .form {
      display: flex;
      flex-direction: column;
      gap: 6px;

      & > .content {
        display: flex;
        flex-direction: column;
        gap: 8px;
        overflow-wrap: anywhere;

        & > .list {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        & .muted {
          color: var(--text-muted);
        }

        & > .updated-at,
        & > .created-at {
          gap: 4px;
        }
      }

      & > .about {
        text-wrap-style: balance;
        font-size: 12px;

        & > a {
          text-decoration: underline;
        }
        & > .about-icon {
          display: inline-block !important;
          vertical-align: top;
          color: var(--primary);
        }
      }

      &.--assigned,
      &.--created {
        & > .content {
          @include theme.max-lg {
            display: grid;
            grid-template-columns: repeat(2, calc(50% - 4px));
          }
          @include theme.max-sm {
            display: flex;
          }
        }
      }

      &.--merge-requests {
        .link-request-button {
          width: 100%;

          @include theme.max-lg {
            width: auto;
            margin-left: auto;
          }

          @include theme.max-md {
            width: 100%;
          }
        }
      }
    }
  }
}
</style>
